import React from 'react';
import styled from 'styled-components';

import { useCardContext } from './card-context';
import { CardStaffPickTag } from './card-staff-pick-tag';
import { CardSpecialOffer } from './card-special-offer';
import { CardComingSoonTag } from './card-coming-soon-tag';
import { CardCollectionBadge } from './card-collection-badge';

type CardTagsProps = {
  /** Show "Staff Pick" as a text tag. Defaults to false (grid card shows it overlaid on the image instead). */
  showStaffPick?: boolean;
  /** Top margin (px). Defaults to 16 (grid). List card uses 12. */
  marginTop?: number;
  /** Render inline tags (staff pick, special offer, coming soon) in a horizontal row. Defaults to false (grid stacks vertically). */
  horizontal?: boolean;
};

/** Convenience wrapper rendering all tag primitives. Renders nothing when no tags apply. */
export function CardTags({
  showStaffPick = false,
  marginTop = 16,
  horizontal = false,
}: CardTagsProps = {}): JSX.Element | null {
  const { isStaffPick, isSpecialOffer, isComingSoon, collectionBadge } = useCardContext();

  const hasStaffPickTag = showStaffPick && isStaffPick;
  if (!hasStaffPickTag && !isSpecialOffer && !collectionBadge && !isComingSoon) {
    return null;
  }

  const hasInlineTags = hasStaffPickTag || isSpecialOffer || isComingSoon;

  const inlineTags = (
    <>
      {showStaffPick && <CardStaffPickTag />}
      <CardSpecialOffer />
      <CardComingSoonTag />
    </>
  );

  return (
    <Tags $marginTop={marginTop}>
      {hasInlineTags && (horizontal ? <TagRow>{inlineTags}</TagRow> : inlineTags)}
      <CardCollectionBadge />
    </Tags>
  );
}

const Tags = styled.div<{ $marginTop: number }>`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  line-height: ${13 / 12};
  margin-top: ${({ $marginTop }) => $marginTop}px;
  text-transform: ${({ theme }) => theme.customized.textTransforms.tags};
`;

const TagRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;
